/**
 * The Explorer chart + heatmap matrix, rendered in ONE shared-geometry
 * container so the chart's bars line up exactly with the table's
 * period columns (and scroll together when the grain overflows).
 *
 * How the alignment works: the table uses table-layout:fixed with a
 * <colgroup> of known widths (label 160 + total 80 + N equal period
 * columns). The chart above is given the SAME total pixel width and a
 * left margin of (label + total - yAxisWidth), so its plot area spans
 * exactly the period-columns region - recharts then divides the plot
 * into N equal category bands, each precisely one column wide, and
 * centres a bar in each.
 */
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import { fmtGbpShort } from "../staff/CommissionedPanels";
import { PALETTE, metricValue, fmtPeriod } from "./explorerUtil";

const LABEL_W = 160;
const TOTAL_W = 80;
const MIN_PERIOD_W = 56;
const Y_AXIS_W = 54;

const fmtCell = (v, metric) => {
  if (v == null) return "–";
  if (metric === "margin") return `${Math.round(v)}%`;
  return fmtGbpShort(v);
};

const heat = (v, metric, maxAbs) => {
  if (v == null || v === 0) return "transparent";
  if (metric === "margin") {
    if (v < 0) return `rgba(225,29,72,${Math.min(Math.abs(v) / 50, 1) * 0.35 + 0.06})`;
    return `rgba(13,148,136,${Math.min(v / 70, 1) * 0.4 + 0.04})`;
  }
  const a = Math.min(Math.abs(v) / (maxAbs || 1), 1) * 0.42 + 0.04;
  return v < 0 ? `rgba(225,29,72,${a})` : `rgba(13,148,136,${a})`;
};

export default function ExplorerMatrix({
  data, metric, dimLabel, onCell, chart, additive, fmtVal,
}) {
  const { periods, rows, column_totals, current_period } = data;

  // Measure the scroll container so period columns stretch to fill
  // the card on wide screens, and only overflow (with the chart
  // scrolling in lock-step) when the grain genuinely doesn't fit.
  const scrollRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = periods.length || 1;
  const periodW = Math.max(
    MIN_PERIOD_W,
    Math.floor(((containerW || 960) - LABEL_W - TOTAL_W) / n),
  );
  const totalWidth = LABEL_W + TOTAL_W + periodW * n;
  const chartMargin = { top: 8, right: 0, left: LABEL_W + TOTAL_W - Y_AXIS_W, bottom: 4 };
  const axisTick = { fontSize: 10, fill: "#a8a29e" };

  const maxAbs = useMemo(() => {
    let m = 0;
    for (const r of rows) {
      for (const p of periods) {
        const v = metricValue(r.cells[p], metric);
        if (v != null && Math.abs(v) > m) m = Math.abs(v);
      }
    }
    return m;
  }, [rows, periods, metric]);

  return (
    <div ref={scrollRef} className="overflow-x-auto" data-testid="explorer-matrix">
      {chart && chart.points.length > 0 && (
        <div data-testid="explorer-chart" style={{ width: totalWidth }}>
          {additive ? (
            <BarChart width={totalWidth} height={230} data={chart.points} margin={chartMargin}>
              <XAxis dataKey="period" hide />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={axisTick} axisLine={false} tickLine={false} width={Y_AXIS_W} />
              <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v, nm) => [fmtVal(v), nm]} />
              {chart.series.map((s, i) => (
                <Bar
                  key={s} dataKey={s} stackId="a"
                  fill={PALETTE[i % PALETTE.length]}
                  maxBarSize={Math.max(10, Math.min(26, periodW - 10))}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart width={totalWidth} height={230} data={chart.points} margin={chartMargin}>
              <XAxis dataKey="period" hide />
              <YAxis tickFormatter={(v) => (metric === "margin" ? `${v}%` : fmtGbpShort(v))} tick={axisTick} axisLine={false} tickLine={false} width={Y_AXIS_W} />
              <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v, nm) => [fmtVal(v), nm]} />
              {chart.series.map((s, i) => (
                <Line key={s} dataKey={s} stroke={PALETTE[i % PALETTE.length]} strokeWidth={1.5} dot={false} connectNulls />
              ))}
            </LineChart>
          )}
        </div>
      )}

      <table
        className="text-[11px] border-collapse"
        style={{ tableLayout: "fixed", width: totalWidth }}
      >
        <colgroup>
          <col style={{ width: LABEL_W }} />
          <col style={{ width: TOTAL_W }} />
          {periods.map((p) => (
            <col key={p} style={{ width: periodW }} />
          ))}
        </colgroup>
        <thead>
          <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-200">
            <th className="sticky left-0 bg-white z-10 py-1.5 pr-2 text-left font-medium">{dimLabel}</th>
            <th className="py-1.5 px-2 text-right font-medium border-r border-stone-200">Total</th>
            {periods.map((p) => (
              <th key={p} className={`py-1.5 px-1.5 text-right font-medium whitespace-nowrap overflow-hidden ${p === current_period ? "text-[#1B4F8A]" : ""} ${p > current_period ? "text-sky-400" : ""}`}>
                {fmtPeriod(p)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-stone-50 last:border-0" data-testid={`explorer-row-${r.key}`}>
              <td className="sticky left-0 bg-white z-10 py-1 pr-2 font-medium text-slate-800 truncate">
                {r.label}
              </td>
              <td className="py-1 px-2 text-right font-mono tabular-nums font-semibold text-slate-800 border-r border-stone-200">
                {fmtCell(metricValue(r.total, metric), metric)}
              </td>
              {periods.map((p) => {
                const c = r.cells[p];
                const v = metricValue(c, metric);
                const clickable = c && r.key !== "__others__";
                return (
                  <td
                    key={p}
                    onClick={clickable ? () => onCell(r, p) : undefined}
                    className={`py-1 px-1.5 text-right font-mono tabular-nums ${v == null ? "text-stone-300" : "text-slate-800"} ${clickable ? "cursor-pointer hover:ring-1 hover:ring-inset hover:ring-[#1B4F8A]" : ""}`}
                    style={{ backgroundColor: heat(v, metric, maxAbs) }}
                    title={c ? `${r.label} · ${fmtPeriod(p)} · ${c.count} invoice${c.count === 1 ? "" : "s"}` : undefined}
                  >
                    {fmtCell(v, metric)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-stone-200">
            <td className="sticky left-0 bg-white z-10 py-1.5 pr-2 font-mono text-[9px] uppercase tracking-wider text-stone-400">Total</td>
            <td className="py-1.5 px-2 text-right font-mono tabular-nums font-semibold text-[#1B4F8A] border-r border-stone-200">
              {fmtCell(metricValue(data.grand_total, metric), metric)}
            </td>
            {periods.map((p) => (
              <td key={p} className="py-1.5 px-1.5 text-right font-mono tabular-nums text-stone-600">
                {fmtCell(metricValue(column_totals[p], metric), metric)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
